import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { useFormat, useWeekdayNames, type AnalyticsMeta } from '../analytics';
import type { ReportResult } from '../reporting-types';
import { displayColumns, dimensionValue, formatCell } from './reportTable';

/**
 * The result of `POST /reporting/preview` or `/reporting/export`'s dry run —
 * one table, whatever the definition's layout produced. Pivot and tabular
 * share this renderer because both answer the same `ReportColumn[]` /
 * `ReportRow[]` shape (ADR-0013 §2.5): pivot is shaping, not a second query
 * path, so it never needs a second table component either.
 */
export function ReportPreview({
  result,
  loading,
  error,
  currency,
  meta,
  onRetry,
}: {
  result: ReportResult | null;
  loading: boolean;
  error: string | null;
  currency: string | null;
  meta: AnalyticsMeta | null;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const format = useFormat(currency);
  const weekday = useWeekdayNames();

  const dimensionMeta = useMemo(
    () => new Map((meta?.dimensions ?? []).map((d) => [d.name, d])),
    [meta],
  );

  const columns = useMemo(() => (result ? displayColumns(result.columns) : []), [result]);

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;
  if (!result) {
    return (
      <EmptyState
        title={t('reporting.builder.noPreview')}
        hint={t('reporting.builder.noPreviewHint')}
      />
    );
  }
  if (!result.rows.length) {
    return (
      <EmptyState title={t('reports.empty.title')} hint={t('reports.empty.hint')} />
    );
  }

  return (
    <div className="space-y-2">
      <TableWrap>
        <Table>
          <THead>
            <TR>
              {columns.map((column) => (
                <TH
                  key={column.key}
                  className={column.kind === 'dimension' ? undefined : 'text-end'}
                >
                  {column.kind === 'dimension'
                    ? t(`reports.dimensions.${column.key}`, column.label)
                    : column.label}
                </TH>
              ))}
            </TR>
          </THead>
          <tbody>
            {result.rows.map((row, index) => {
              const key = columns
                .filter((c) => c.kind === 'dimension')
                .map((c) => String(dimensionValue(row, c.key) ?? ''))
                .join('|');
              return (
                <TR key={key || index}>
                  {columns.map((column) => (
                    <TD
                      key={column.key}
                      className={column.kind === 'dimension' ? undefined : 'text-end tabular-nums'}
                    >
                      {column.kind === 'dimension'
                        ? formatCell(dimensionValue(row, column.key), column, format, weekday, dimensionMeta)
                        : formatCell(row[column.key] ?? null, column, format, weekday, dimensionMeta)}
                    </TD>
                  ))}
                </TR>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>
      {result.truncated && (
        <p role="status" className="text-xs text-ink-muted">
          {t('reporting.builder.truncated', { count: result.rowCount })}
        </p>
      )}
    </div>
  );
}
